/**
 * Beginner Mode: How to Play, how a game goes (deploy, wars, turns and their steps, end of round, repeat).
 * Shown once per game before the first Beginner screen (the war lesson or your card).
 *
 * The title shows alone for a second, then the steps show one at a time, each with a small example
 * right under it made of cards and dice: cards flipping (Deploy), matching cards and a war roll (War), the turn going
 * round from the Dealer (Turns), dice appearing (Dice Roll), a power moving a die (Power), a kill,
 * dice held, the turn passing on (End Turn), the Dealer moving on (End Round) and cards knocked
 * out until one player is left (Repeat). Winning dice blink green and grow (the war's 5, the
 * killing 4s); the winning card lunges at each card it beats and knocks it out (War, Kill, Repeat). Roll, Power and Kill are one story: the Knight of 3's rolls a
 * 3, makes it a 4 (its 3s are wild) and kills the Cleric (four of a kind) with those dice; Hold Dice,
 * the other choice, keeps three 4s toward the Cleric. Each step waits for Next, which is never greyed out
 * (pressed mid-example it moves straight on); Next on the last step closes.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { MAX_SEQUENCES, t, type DeckColor } from '@kadice/shared';
import { cardImage } from '../cards';
import { Dice } from './Dice';

const STEPS = ['deploy', 'war', 'turns', 'roll', 'power', 'kill', 'hold', 'end', 'round', 'repeat'] as const;
type StepId = (typeof STEPS)[number];
const LAST = STEPS.length - 1;

/** The title and list show alone this long before the first step. */
const INTRO_MS = 1000;
/** A step fades out this long after Next (matches `.seq-live` in the CSS). */
const FADE_MS = 300;
/** How often the timeline is checked. */
const TICK_MS = 50;
/** A lunge lands this long after it starts (matches `seq-lunge` in the CSS): the card hit goes out then. */
const KO_HIT_MS = 200;

/** The example cards: three players' characters (the war uses one character in two colours).
 * The first is the Knight of 3's (power: 3s are wild), the second the Cleric (Kill Quest: four of a kind). */
const PLAYERS: readonly (readonly [DeckColor, number])[] = [
  ['blue', 3],
  ['red', 9],
  ['green', 12],
];
const WAR: readonly (readonly [DeckColor, number])[] = [
  ['blue', 5],
  ['red', 5],
];
/** The Knight's roll: one 3 short of the Cleric's four of a kind. */
const ROLL = [4, 3, 6, 4, 4];
/** The 3 (wild) made a 4. */
const WILD = 1;
const POWERED = ROLL.map((v, i) => (i === WILD ? 4 : v));
/** The four 4s that kill the Cleric. */
const KILLERS = [0, 1, 3, 4];
/** Hold Dice instead: a roll with no kill, three 4s kept toward the Cleric. */
const HOLD_ROLL = [4, 6, 4, 1, 4];
const HOLD = [0, 2, 4];

/**
 * One example card: face up or its deck's back (`back` given: it flips when that changes), with an
 * optional ring, Dealer chip or fade. `lunge`: it lunges at the card that many places to its right
 * (a knockout; that card goes `out` as it lands). `out` cards are knocked askew and faded.
 */
function Card({
  of: [color, id],
  back,
  turn,
  dealer,
  out,
  won,
  lunge,
}: {
  of: readonly [DeckColor, number];
  back?: boolean | undefined;
  turn?: boolean;
  dealer?: boolean;
  out?: boolean;
  won?: boolean;
  lunge?: 1 | 2 | undefined;
}) {
  return (
    <span
      className={`seq-card${back !== undefined ? ' flips' : ''}${turn ? ' turn' : ''}${out ? ' out' : ''}${won ? ' won' : ''}${lunge ? ` lunge-${lunge}` : ''}`}
    >
      {/* Keyed by side, so turning over plays the flip. */}
      <img key={back ? 'b' : 'f'} src={cardImage(color, back ? 'back' : id)} alt="" className="card" />
      {dealer && <img key="dealer" src="/img/dealer-button.png" alt="" className="seq-dealer" />}
    </span>
  );
}

/** Five dice, the first `shown` of them showing (the rest keep their place). */
const some = (values: readonly number[], shown: number) => values.map((v, i) => (i < shown ? v : 0));

/** A step's example, `at` ms into it: a row of cards and, under it, a row of dice (or its room). */
function Example({ step, at }: { step: StepId; at: number }) {
  const after = (ms: number) => at >= ms;
  let cards: ReactNode;
  let dice: ReactNode = null;
  switch (step) {
    case 'deploy':
      cards = PLAYERS.map((p, i) => <Card key={i} of={p} back={!after(600 + i * 500)} />);
      break;
    case 'war':
      // The 5 beats the 2 (blinks green, grows; the 2 shrinks), then blue knocks red out.
      cards = WAR.map((p, i) => (
        <Card
          key={i}
          of={p}
          turn={after(400) && !after(1900)}
          out={i === 1 && after(KO_HIT_MS + 1500)}
          lunge={i === 0 && after(1500) ? 1 : undefined}
        />
      ));
      dice = (
        <div className="seq-war-dice">
          <Dice values={[after(1000) ? 5 : 0]} winning={after(1300) ? [0] : []} small />
          <Dice values={[after(1150) ? 2 : 0]} losing={after(1300) ? [0] : []} small />
        </div>
      );
      break;
    case 'turns':
      cards = PLAYERS.map((p, i) => <Card key={i} of={p} dealer={i === 0} turn={Math.floor(at / 900) % 3 === i} />);
      break;
    case 'roll':
      cards = <Card of={PLAYERS[0]!} turn />;
      dice = <Dice values={some(ROLL, Math.floor((at - 300) / 350) + 1)} small />;
      break;
    case 'power':
      cards = <Card of={PLAYERS[0]!} turn />;
      dice = (
        <Dice
          values={after(1700) ? POWERED : ROLL}
          nudge={{ index: WILD, dir: after(800) && !after(1700) ? 'up' : null }}
          small
        />
      );
      break;
    case 'kill':
      // The four 4s blink green and grow, then the Knight knocks the Cleric out.
      cards = [
        <Card key={0} of={PLAYERS[0]!} turn lunge={after(1400) ? 1 : undefined} />,
        <Card key={1} of={PLAYERS[1]!} out={after(KO_HIT_MS + 1400)} />,
      ];
      dice = <Dice values={POWERED} lifted={after(600) ? KILLERS : []} winning={after(600) ? KILLERS : []} small />;
      break;
    case 'hold':
      cards = <Card of={PLAYERS[0]!} turn />;
      dice = <Dice values={HOLD_ROLL} held={HOLD.filter((_, i) => after(700 + i * 500))} small />;
      break;
    case 'end':
      cards = PLAYERS.map((p, i) => <Card key={i} of={p} turn={i === (after(1200) ? 1 : 0)} />);
      break;
    case 'round':
      cards = PLAYERS.map((p, i) => <Card key={i} of={p} dealer={i === (after(1200) ? 1 : 0)} />);
      break;
    case 'repeat':
      // The winner knocks the other two out, one after the other.
      cards = PLAYERS.map((p, i) => (
        <Card
          key={i}
          of={p}
          out={(i === 1 && after(KO_HIT_MS + 500)) || (i === 2 && after(KO_HIT_MS + 1200))}
          won={i === 0 && after(2100)}
          lunge={i === 0 && after(500) ? (after(1200) ? 2 : 1) : undefined}
        />
      ));
      break;
  }
  return (
    <>
      <div className="seq-cards">{cards}</div>
      <div className="seq-dice">{dice}</div>
    </>
  );
}

/** One step: its name in bold, then what happens (Hold Dice is the other choice to Kill, so it says so first). */
const Step = ({ id }: { id: StepId }) => (
  <p className="seq-step">
    {id === 'hold' && <span className="sequence-or">{t('sequence.or')}</span>}
    <span>
      <strong>{t(`sequence.${id}`)}</strong>{' '}
      {t(`sequence.${id}Text`, id === 'round' ? { n: MAX_SEQUENCES } : undefined)}
    </span>
  </p>
);

export function SequenceGuide({ onDone }: { onDone: () => void }) {
  // The step showing (-1: the title alone) and when it started; ms into it follow from the clock.
  const [{ index, since }, setStep] = useState({ index: -1, since: 0 });
  const [now, setNow] = useState(0);
  // Fading out after Next; the next step shows once it has.
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    const timer = setInterval(() => setNow(performance.now()), TICK_MS);
    const intro = setTimeout(() => setStep({ index: 0, since: performance.now() }), INTRO_MS);
    return () => {
      clearInterval(timer);
      clearTimeout(intro);
    };
  }, []);
  useEffect(() => {
    if (!leaving) return;
    const timer = setTimeout(() => {
      setLeaving(false);
      setStep((s) => ({ index: s.index + 1, since: performance.now() }));
    }, FADE_MS);
    return () => clearTimeout(timer);
  }, [leaving]);

  const lit = index < 0 ? null : STEPS[index]!;
  const at = Math.max(0, now - since);

  return (
    <div className="modal-backdrop">
      <div className="modal splash sequence-guide" role="dialog" aria-modal="true">
        <h2>{t('sequence.title')}</h2>
        {/* One step at a time, its example right under it. The stage keeps one height for every
            step (an invisible sizer: every text stacked, the tallest wins, plus the example's
            room), so the box and Next never move; the spare room is below. Keyed per step, so
            each fades in; it fades out after Next. */}
        <div className="seq-stage">
          <div className="seq-sizer" aria-hidden>
            <div className="seq-texts">
              {STEPS.map((id) => (
                <Step key={id} id={id} />
              ))}
            </div>
            <div className="seq-room" />
          </div>
          {lit && (
            <div key={lit} className={`seq-live${leaving ? ' leaving' : ''}`}>
              <Step id={lit} />
              <div className="seq-ex" aria-hidden>
                <Example step={lit} at={at} />
              </div>
            </div>
          )}
        </div>
        <div className="row actions">
          <button className="primary intro-next" onClick={() => (index === LAST ? onDone() : setLeaving(true))}>
            {t('common.next')}
          </button>
        </div>
      </div>
    </div>
  );
}
